import { useMemo } from 'react';
import { gql } from '@apollo/client';

import { getActiveTrace } from '@atlaskit/react-ufo/experience-trace-id-context';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { Extension } from '@atlassian/forge-ui';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { fg } from '@confluence/feature-gating';

import type { ForgeUIExtensionDataType } from '../types.ts';
import { getModuleKey } from '../utils/appData.ts';

type GetContextToken = (contextIdsOverride?: string[]) => Promise<string>;

// Explicit types to satisfy TS inference for the mutation result
export type UseGetContextTokenMutationData = {
	confluence_generateForgeContextToken?: {
		success: boolean;
		errors?: Array<{
			message: string;
			code?: string;
		}>;
		forgeContextToken?: {
			jwt?: string;
			expiresAt?: string | null;
			extensionId?: string | null;
		};
	};
};

export type UseGetContextTokenMutationVariables = {
	cloudId: string;
	input: any;
};

// Cache types and helpers
export type CachedTokenPromise = {
	promise: Promise<string>;
	expiresAt?: number;
	issuedAt: number;
};

class ContextTokenCache {
	private static instance: ContextTokenCache;
	private promiseCache: Map<string, CachedTokenPromise>;

	static getInstance() {
		if (!ContextTokenCache.instance) {
			ContextTokenCache.instance = new ContextTokenCache();
		}
		return ContextTokenCache.instance;
	}

	private constructor() {
		this.promiseCache = new Map<string, CachedTokenPromise>();
	}

	get(key: string): CachedTokenPromise | undefined {
		return this.promiseCache.get(key);
	}

	set(key: string, value: CachedTokenPromise): void {
		this.promiseCache.set(key, value);
	}

	delete(key: string): void {
		this.promiseCache.delete(key);
	}
}

const EXTRA_BUFFER_MS = 30000; // extra 30 seconds buffer for clock skew

// GraphQL mutation to get context token
const UseGetContextTokenMutation = gql`
	mutation useGetContextTokenMutation(
		$cloudId: ID!
		$input: ConfluenceForgeContextTokenRequestInput!
	) {
		confluence_generateForgeContextToken(cloudId: $cloudId, input: $input) {
			success
			errors {
				message
			}
			forgeContextToken {
				jwt
				expiresAt
				extensionId
			}
		}
	}
`;

/**
 * Hook to generate a context token function for Forge extensions in Confluence
 *
 * @param extension - The Forge extension configuration
 * @param extensionData - The extension context data
 * @param localId - The local identifier for this extension instance
 * @param contextIds - The context IDs for this extension instance
 * @returns A function that can be called to retrieve context tokens
 */
export const useGetContextToken = (
	extension: Extension,
	extensionData: ForgeUIExtensionDataType,
	localId: string | undefined,
	contextIds: string[],
): GetContextToken => {
	const { cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [runMutation] = useMutationInterop<
		UseGetContextTokenMutationData,
		UseGetContextTokenMutationVariables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(UseGetContextTokenMutation, {
		client: getAGGClient(),
	});

	return useMemo(() => {
		const sendEvent = (action: 'succeeded' | 'failed', attributes: Record<string, unknown>) => {
			if (!fg('confluence_frontend_forge_context_token_metrics')) return;
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action,
					actionSubject: 'forgeContextToken',
					source: 'useGetContextToken',
					attributes: {
						traceId: getActiveTrace()?.traceId || 'unknown',
						...attributes,
					},
				},
			}).fire();
		};

		const fetchTokenFromServer = async (
			effectiveContextIds: string[],
			cache: ContextTokenCache,
			cacheKey: string,
		): Promise<string> => {
			try {
				// Build the input according to the schema
				const input = {
					contextIds: effectiveContextIds,
					extensionSpecificContexts: {
						appVersion: extension.appVersion,
						context: {
							appVersion: extension.appVersion,
							environmentId: extension.environmentId,
							environmentType: extension.environmentType,
							siteUrl: window?.location?.origin,
							extension: {
								location: undefined, //extensionData?.location
								content: extensionData?.content
									? {
											id: extensionData.content.id,
											type: extensionData.content.type,
											subtype: extensionData.content.subtype,
										}
									: undefined,
								space: extensionData?.space
									? {
											id: extensionData.space.id,
											key: extensionData.space.key,
										}
									: undefined,
								...(extensionData?.template &&
								fg('forge-ui-includes-template-editor-extension-data')
									? { template: { id: extensionData.template.id } }
									: {}),
								type: extensionData.type,
								selectedText: extensionData?.selectedText,
							},
							localId,
							moduleKey: getModuleKey(extension),
						},
						extensionId: extension.id,
						extensionType: extension.type,
						installationId: extension.installationId,
					},
				};

				const { data } = await runMutation({
					variables: {
						cloudId,
						input,
					},
				});

				if (!data?.confluence_generateForgeContextToken?.success) {
					const errors = data?.confluence_generateForgeContextToken?.errors || [];
					const errorMessage = errors.map((e) => e.message).join(', ') || 'Unknown error';
					throw new Error(`Token generation failed: ${errorMessage}`);
				}

				const forgeContextToken = data.confluence_generateForgeContextToken.forgeContextToken;
				if (!forgeContextToken?.jwt) {
					throw new Error('No JWT token received from GraphQL endpoint');
				}

				// Update cache with expiry info if available
				const cachedEntry = cache.get(cacheKey);
				if (cachedEntry && forgeContextToken.expiresAt) {
					cachedEntry.expiresAt = Number(forgeContextToken.expiresAt);
				}

				// Analytics: success (do not include raw JWT)
				sendEvent('succeeded', {
					extensionId: forgeContextToken.extensionId,
					moduleKey: getModuleKey(extension),
					hasJwt: Boolean(forgeContextToken.jwt),
					expiresAt: forgeContextToken.expiresAt,
				});

				return forgeContextToken.jwt;
			} catch (error: any) {
				// Remove from cache on error so next call will retry
				cache.delete(cacheKey);

				// Analytics: failure
				sendEvent('failed', {
					message: error?.message,
					extensionId: extension?.id,
					moduleKey: getModuleKey(extension),
					contextIds: effectiveContextIds,
				});
				throw new Error(`Failed to get context token: ${error.message}`);
			}
		};

		return async function getForgeContextToken(contextIdsOverride?: string[]): Promise<string> {
			if (!cloudId) {
				throw new Error('CloudId is required but not available');
			}

			const effectiveContextIds = contextIdsOverride ?? contextIds;
			const cacheKey = JSON.stringify({
				id: localId || extension.id,
				data: extensionData,
				contextIds: effectiveContextIds,
			});
			const cache = ContextTokenCache.getInstance();
			const cached = cache.get(cacheKey);

			// Check if cached promise is still valid
			if (cached) {
				// If no expiresAt provided, assume token is valid and return cached promise
				if (typeof cached.expiresAt !== 'number') {
					return cached.promise;
				}

				if (Date.now() + EXTRA_BUFFER_MS < cached.expiresAt) {
					return cached.promise;
				}
			}

			// Create and cache a new promise
			const issuedAt = Date.now();
			const tokenPromise = fetchTokenFromServer(effectiveContextIds, cache, cacheKey);

			// Cache the promise immediately
			cache.set(cacheKey, {
				promise: tokenPromise,
				expiresAt: undefined, // Will be updated when promise resolves
				issuedAt,
			});

			return tokenPromise;
		};
	}, [extension, extensionData, localId, cloudId, runMutation, contextIds, createAnalyticsEvent]);
};
